/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useLDClient } from 'launchdarkly-react-client-sdk'

import { useProfile } from '@modules/profile-lib/hooks'
import type { UserProfileFromApi } from '@modules/ui-types'

// eslint-disable-next-line import/no-restricted-paths
import { getConfigForKey } from '@/store'
// eslint-disable-next-line import/no-restricted-paths
import AppLoadingRoot from '@/components/AppLoadingRoot'

import { LaunchDarklyContext } from './LaunchDarklyContext'

import { makeUserLDContext } from '.'

import type { LDContext } from 'launchdarkly-react-client-sdk'

type InitializationStatus =
  | 'uninitialized'
  | 'initializing'
  | 'pending-org'
  | 'completed'
  | 'failed'

const shouldNotInitializeAgain: InitializationStatus[] = ['initializing', 'completed', 'failed']
const shouldShowLoading: InitializationStatus[] = ['uninitialized', 'initializing']
const isClientInitialized: InitializationStatus[] = ['completed', 'pending-org']

const anonymousContext = {
  kind: 'user',
  anonymous: true,
} as const

type GetContextProps = {
  profile?: UserProfileFromApi
  isAnonymous: boolean
}

const getContext = ({ profile, isAnonymous }: GetContextProps) => {
  if (isAnonymous) {
    return anonymousContext
  }

  if (!profile) {
    return null
  }

  return makeUserLDContext(profile)
}

type GetCompletedStatusProps = GetContextProps

const getCompletedStatus = ({
  profile,
  isAnonymous,
}: GetCompletedStatusProps): InitializationStatus => {
  if (isAnonymous) {
    return 'completed'
  }

  return profile?.organization_id ? 'completed' : 'pending-org'
}

/**
 * We make the distinction between any initialization (LD is ready to consume)
 * and "complete" initialization (LD has been sent all the information we want
 * to send it, i.e. org ID). Ideally this is done in one execution of the
 * useEffect function below, but if the user doesn't have an associated org at
 * the time of the first initialization, we want to update the LD association
 * when one becomes associated.
 */
export const LaunchDarklyProvider: React.FunctionComponent = ({ children }) => {
  const timeout = getConfigForKey('LAUNCHDARKLY_CLIENT_INITIALIZATION_TIMEOUT_SECONDS') ?? 5
  const [isAnonymous, setIsAnonymous] = useState<boolean>(true)
  const ldClient = useLDClient()
  const profile = useProfile({ enabled: !isAnonymous })

  const [initializationStatus, setInitializationStatus] =
    useState<InitializationStatus>('uninitialized')
  const [isIdentifying, setIsIdentifying] = useState<boolean>(false)

  useEffect(() => {
    const catastrophicErrorTimer = setTimeout(
      () => {
        // If for whatever reason we don't have the LDClient after 2 x timeout,
        // we can assume there's something wrong with the LD SDK and force the
        // initialization status to prevent infinite loading
        if (!ldClient) {
          setInitializationStatus('failed')
        }
      },
      timeout * 2 * 1000,
    )

    return () => {
      clearTimeout(catastrophicErrorTimer)
    }
  }, [ldClient, timeout])

  useEffect(() => {
    const initializeLaunchDarkly = async () => {
      if (!ldClient) {
        return
      }

      if (shouldNotInitializeAgain.includes(initializationStatus)) {
        return
      }

      if (initializationStatus === 'pending-org' && profile?.organization_id) {
        return
      }

      const context = getContext({ profile, isAnonymous })

      if (!context) {
        return
      }

      try {
        setInitializationStatus('initializing')
        await ldClient.waitForInitialization(timeout)

        setIsIdentifying(true)
        await ldClient.identify(context)

        setInitializationStatus(getCompletedStatus({ profile, isAnonymous }))
      } catch (error) {
        setInitializationStatus('failed')
      } finally {
        setIsIdentifying(false)
      }
    }

    initializeLaunchDarkly()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ldClient, profile])

  const identify = useCallback(
    async (context: LDContext) => {
      if (!ldClient) {
        return
      }

      if (!isClientInitialized.includes(initializationStatus)) {
        return
      }

      try {
        setIsIdentifying(true)
        await ldClient.identify(context)
      } catch (error) {
        console.warn('Failed to identify context: ', error)
      } finally {
        setIsIdentifying(false)
      }
    },
    [ldClient, initializationStatus],
  )

  if (shouldShowLoading.includes(initializationStatus)) {
    return <AppLoadingRoot />
  }

  return (
    <LaunchDarklyContext.Provider
      value={{
        clientInitialized: isClientInitialized.includes(initializationStatus),
        clientInitializationFailed: initializationStatus === 'failed',
        isIdentifying,
        identify,
        setIsAnonymous,
      }}
    >
      {children}
    </LaunchDarklyContext.Provider>
  )
}
